import { ReactNode } from 'react';
import { MenuNode } from 'src/model/MenuNode';
import { HierarchyAccessors, HierarchyAction } from './hierarchyTypes';

export interface DashboardMenuNode extends MenuNode {
  children?: DashboardMenuNode[];
  titleText?: string;
  label?: ReactNode;
  dashboard?: boolean;
  group?: boolean;
  value?: string;
  selected?: boolean;
  graph?: boolean;
  table?: boolean;
  loading?: boolean;
  url?: string;
}

const ROOT = 'root';

function insertBeforeDivider(parent: DashboardMenuNode, child: DashboardMenuNode): void {
  if (!parent.children) parent.children = [];
  const idx = parent.children.findIndex(c => c.key === MenuNode.GROUPS_DIVIDER_KEY);
  if (idx >= 0) parent.children.splice(idx, 0, child);
  else parent.children.push(child);
  parent.isLeaf = false;
}

export const menuDataNodeAccessors: HierarchyAccessors<DashboardMenuNode> = {
  getId: node => String(node.itemId),
  getLabel: node => node.titleText ?? (typeof node.label === 'string' ? node.label : String(node.title ?? '')),
  getChildren: node => node.children,
  isLeaf: node => Boolean(node.isLeaf),
  isRoot: node => node.itemId === ROOT,
  isDashboard: node => Boolean(node.dashboard),
  isGroup: node => Boolean(node.group),
  getGroupId: node => MenuNode.getGroupId(node) ?? undefined,
  // The menu API sets group_id only on the shared-branch root node itself.
  isGroupRoot: node => node.group_id != null,
  equals: (a, b) => {
    if (a === b) return true;
    if (a && b) return Boolean(a.itemId && a.itemId === b.itemId) || Boolean(a.key && a.key === b.key);
    return false;
  },
  makeChild: (created, type, parent, childUrl) => {
    const node: DashboardMenuNode = {
      key: created.id,
      value: created.id,
      itemId: created.id,
      title: created.label as ReactNode,
      titleText: created.label,
      label: undefined,
      dashboard: type === HierarchyAction.DASHBOARD,
      group: type === HierarchyAction.GROUP,
      isLeaf: type === HierarchyAction.GROUP,
      url: childUrl,
      parent,
    };
    if (!node.isLeaf) node.children = [];
    return node;
  },
  insertChild: insertBeforeDivider,
  // Root-level creates (nav menu's synthetic root): personal nodes stay above the divider.
  insertTopLevel: (nodes, child) => {
    const idx = nodes.findIndex(c => c.key === MenuNode.GROUPS_DIVIDER_KEY);
    if (idx >= 0) nodes.splice(idx, 0, child);
    else nodes.push(child);
  },
};
